import React, { ReactElement, useEffect, useState } from 'react';
import { notification, Space, Table, Typography } from 'antd';
import { isEmpty } from 'lodash';

import { RemoveBreederAdminManagedBreeder } from 'dogpark/components';
import { useBreederAdminsContext } from 'dogpark/context';
import { getBreederAdminManagedBreeders } from 'dogpark/utilities/breeders';
import { BreederAdminUser, User } from 'dogpark/types';

const { Text } = Typography;

interface Props {
  breederAdmin: BreederAdminUser;
}

function BreederAdminsExpandableRowContent(props: Props): ReactElement {
  const {
    breederAdmin: { id, managedBreeders },
  } = props;
  const { dispatch } = useBreederAdminsContext();
  const [loadingManagedBreeders, setLoadingManagedBreeders] = useState(false);

  useEffect(() => {
    const handleFetchManagedBreeders = async () => {
      try {
        setLoadingManagedBreeders(true);
        const respManagedBreeders = await getBreederAdminManagedBreeders(id);

        dispatch({
          type: 'SET_BREEDER_ADMIN_MANAGED_BREEDERS',
          payload: { userId: id, managedBreeders: respManagedBreeders },
        });
      } catch (e) {
        e.message &&
          notification.error({ key: 'getBreederAdminsError', message: e.message });
      } finally {
        setLoadingManagedBreeders(false);
      }
    };

    id && isEmpty(managedBreeders) && handleFetchManagedBreeders();
  }, [id]);

  return (
    <section>
      <Table
        size="small"
        loading={loadingManagedBreeders}
        dataSource={managedBreeders}
        rowKey={(record) => record.id}
        columns={[
          { title: 'ID', dataIndex: 'id', width: '135px' },
          {
            title: 'Breeder Name',
            dataIndex: 'primaryUser',
            render: (record) => `${record.firstName} ${record.lastName}`,
          },
          {
            title: 'Status',
            dataIndex: 'primaryUser',
            render: ({ isActive, pivot }: User) => {
              const userIsActive = Boolean(isActive && pivot.isPrimary);
              return (
                <Text type={userIsActive ? 'success' : 'secondary'}>
                  {userIsActive ? 'Active' : 'Inactive'}
                </Text>
              );
            },
          },
          {
            key: 'action',
            align: 'right',
            render: (record) => (
              <Space>
                <RemoveBreederAdminManagedBreeder breederId={record.id} />
              </Space>
            ),
          },
        ]}
        scroll={{ x: true }}
        pagination={{
          hideOnSinglePage: true,
          pageSize: 10,
        }}
      />
    </section>
  );
}

export default BreederAdminsExpandableRowContent;
